import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";

import { ProductImage } from "../ComposableProductTile/__internal__/__internal__/components/ProductImage/ProductTileImage";

import { BrandInfo } from "./__internal__/components/BrandInfo";
import { ProductLink } from "./__internal__/components/ProductLink";
import { ProductTileNameAndPrice } from "./__internal__/components/ProductTileNameAndPrice";
import { ProductTileOverlay } from "./__internal__/components/ProductTileOverlay";
import { ProductTileProductInformation } from "./__internal__/components/ProductTileProductInformation";
import { PromotedTextBadge } from "./__internal__/components/PromotedBadge";
import {
  BaseProductTileProps,
  productTileAdapter,
} from "./__internal__/ProductTilePropAdapter";
import { ProductTileTemplate } from "./__internal__/ProductTileTemplate";
import { IProductTileImageConfigProps, ITileInteractions } from "./types";
import { ILoggingContext, ProductTileType } from "./types";

interface ProductTileProps {
  product?: ILeanProductTile;
  loggingContext: ILoggingContext; // required
  type: ProductTileType; // required
  tileInteractions?: ITileInteractions;
  isLoggedIn: boolean;
  brand?: ILeanProductTile.IBrand;
  imageConfigProps?: IProductTileImageConfigProps;
}

export const ProductTile: React.FC<ProductTileProps> = (props) => {
  if (!props.product || !props.type) {
    throw new Error("ProductTile requires product and type");
  }

  const productTileInformation = productTileAdapter(
    props.product,
    props.isLoggedIn,
    props.type,
    props.brand,
    props.imageConfigProps
  ); // get product tile information based on type

  if (!productTileInformation) {
    return null;
  }

  return (
    <ProductTileTemplate
      loggingContext={{
        ...props.loggingContext, // from surfaces
      }}
      trackingProps={productTileInformation.productTileTrackingWrapperProps}
      tileInteractions={props.tileInteractions} // onClickEvent actions
      topSlot={getTopSlot(productTileInformation)}
      overlaySlot={getOverlaySlot(productTileInformation)}
      middleSlot={getMiddleSlot(productTileInformation, props.type)}
      bottomSlot={getBottomSlot(productTileInformation, props.type)}
    />
  );
};

const getBottomSlot = (information: BaseProductTileProps, type: string) => {
  if (type === ProductTileType.TILE_WITHOUT_BRAND) {
    return (
      <ProductTileProductInformation
        caseLineProps={information.caseLineProps}
        expectedShipDateProps={information.expectedShipDateProps}
      />
    );
  }
  if (type === ProductTileType.TILE_WITH_BRAND) {
    return (
      <>
        <BrandInfo brandInfoProps={information.brandInfoProps} />
        <PromotedTextBadge
          isPromoted={information.productTileTrackingWrapperProps.isPromoted}
          promotedBy={information.productTileTrackingWrapperProps.promotedBy}
        />
      </>
    );
  }
  return null;
};

const getOverlaySlot = (information: BaseProductTileProps) => {
  return (
    <ProductTileOverlay
      productTileQuickAddProps={information.productTileQuickAddProps}
      productTileNotifyMeProps={information.productTileNotifyMeProps}
      productTileFavoriteProps={information.productTileFavoriteProps}
      productTileDebugInfoProps={information.productTileDebugInfoProps}
      topBadges={information.productTileTopBadgesProps}
      productTileOverlayProps={information.productTileOverlayProps}
    />
  );
};

const getTopSlot = (information: BaseProductTileProps) => {
  return (
    <ProductLink dataTestId="product-tile-image">
      <ProductImage data={information.productTileImageProps} />
    </ProductLink>
  );
};

const getMiddleSlot = (information: BaseProductTileProps, type: string) => {
  if (type === ProductTileType.QUICK_ADD_ONLY_TILE) {
    return null;
  }
  return (
    <ProductTileNameAndPrice
      priceTextProps={information.priceTextProps}
      productName={information.productName}
      productTileSwatchesProps={information.productTileSwatchesProps}
    />
  );
};
